import {
  Animated,
  Image,
  StyleSheet,
  Text,
  View,
  ScrollView,
} from "react-native";
import { useEffect, useRef, useState } from "react";
import {
  BtnSidebarClosePng,
  GameRulesLight,
  RulesBackground,
  ModalTitle,
  FlagEnPng,
  ArrowDownPng,
} from "../../../assets";
import EnFlag from "../../../assets/buttonsByLanguage/flags/en_flag.png";
import BgFlag from "../../../assets/buttonsByLanguage/flags/bg_flag.png";
import FrFlag from "../../../assets/buttonsByLanguage/flags/fr_flag.png";
import RuFlag from "../../../assets/buttonsByLanguage/flags/ru_flag.png";
import TrFlag from "../../../assets/buttonsByLanguage/flags/tr_flag.png";

import { useDevice } from "../../../utils/hooks/useDevice";
import { RenderToggle } from "./RenderToggle";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import {
  profileRequest,
  updateProfileRequest,
} from "../../../store/auth/authActions";
import Toast from "react-native-toast-message";
import React from "react";
import { i18nLoadRequest } from "../../../store/i18n/i18nActions";
import { useTranslation } from "react-i18next";
import ModalReusable from "./ModalReusable";
import { LanguagePicker } from "../common/LanguagePicker";
import { analyticsEvents } from "../../../services/firebase/analytics-events.service";

interface IProps {
  onClose: () => void;
}

function SettingsModal({ onClose }: IProps) {
  const { t } = useTranslation();
  analyticsEvents.trackOpenSettings();
  const opacity = useRef(new Animated.Value(0)).current;
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(profileRequest());
  }, [dispatch]);
  const { user } = useSelector((state: RootState) => state.auth);

  const { langCode, loading } = useSelector((s: RootState) => s.i18n);

  const languages = [
    { code: "en", id: 1, label: "English" },
    { code: "bg", id: 2, label: "Български" },
    { code: "fr", id: 3, label: "Français" },
    { code: "ru", id: 4, label: "Русский" },
    { code: "tr", id: 5, label: "Türkçe" },
  ];

  const languageFlags: Record<string, any> = {
    en: EnFlag,
    bg: BgFlag,
    fr: FrFlag,
    ru: RuFlag,
    tr: TrFlag,
  };

  const { isDesktop, width } = useDevice();
  const [sound, setSound] = useState(true);
  const [emailNotif, setEmailNotif] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [lowQuality, setLowQuality] = useState(false);
  const [handMeter, setHandMeter] = useState(false);
  const [facebook, setFacebook] = useState(true);

  useEffect(() => {
    if (!user) return;

    setSound(Boolean(user.isSoundEffectsEnabled));
    setEmailNotif(Boolean(user.isEmailEnabled));
    setNotifications(Boolean(user.isMobileEnabled));
    setHandMeter(Boolean(user.isHandPowerEnabled));
    // setFacebook(Boolean(user.isFacebookLinked))
  }, [user]);

  const updateField = (
    patch: Partial<{
      isSoundEffectsEnabled: boolean;
      isMobileNotificationsEnabled: boolean;
      isEmailNotificationsEnabled: boolean;
      isHandPowerMeterEnabled: boolean;
    }>,
  ) => {
    dispatch(updateProfileRequest(patch as any));

    Toast.show({
      type: "success",
      text1: "Profile updated",
      text2: "Your profile was successfully updated",
      position: "bottom",
      visibilityTime: 2000,
      props: {
        height: 300,
        fontSize: 12,
      },
      text1Style: {
        fontSize: 12,
      },
      text2Style: {
        fontSize: 10,
        color: "#100f14ff",
      },
    });
  };

  useEffect(() => {
    Animated.timing(opacity, {
      toValue: 1,
      duration: 200,
      useNativeDriver: true,
    }).start();

    return () => {
      opacity.setValue(0);
    };
  }, []);

  const handleClose = () => {
    Animated.timing(opacity, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      onClose();
    });
  };
  const SettingRow = ({
    title,
    rightSlot,
    wrapperStyle,
  }: {
    title: string;
    rightSlot: React.ReactNode;
    wrapperStyle?: any;
  }) => (
    <View style={[{ position: "relative" }, wrapperStyle]}>
      <Image
        source={GameRulesLight}
        style={{ width: "100%", height: isDesktop ? 12 : 8, marginBottom: 6 }}
        resizeMode="stretch"
      />

      <View style={styles.row}>
        <Text style={styles.title}>{title}</Text>
        <View style={{ position: "relative", zIndex: 9999 }}>{rightSlot}</View>
      </View>
    </View>
  );

  return (
    <>
      <ModalReusable
        visible
        onClose={onClose}
        // backgroundImage={RulesBackground}
        titleImage={ModalTitle}
        closeIcon={BtnSidebarClosePng}
        title={t("T_SITE_MENU_SETTINGS")}
        titleImageStyles={{ top: -60, width: 240, height: 100 }}
        titleTextStyles={{ width: 240, height: 100 }}
      >
        <ScrollView
          contentContainerStyle={[
            styles.container,
            {
              minWidth: isDesktop ? 600 : 300,
              gap: isDesktop ? 10 : 6,
              overflow: "visible" as any,
            },
          ]}
        >
          <SettingRow
            title={width > 600 ? t("T_SITE_SETTINGS_LANGUAGE") : ""}
            wrapperStyle={{ zIndex: 99999 }}
            rightSlot={
              <LanguagePicker
                value={langCode}
                options={languages.map((l) => ({
                  code: l.code,
                  label: l.label,
                  flag: languageFlags[l.code],
                }))}
                caretIcon={ArrowDownPng}
                containerStyle={{ zIndex: 99999, elevation: 2000 }}
                buttonStyle={{
                  minWidth: 220,
                  marginRight: 20,
                  borderColor: "#ab8b68",
                }}
                onChange={(code) => {
                  const lang = languages.find((l) => l.code === code);
                  if (lang)
                    dispatch(
                      i18nLoadRequest({ langId: lang.id, langCode: lang.code }),
                    );
                }}
                menuPlacement="below"
                showCurrentEnglishLabel={false}
                showMenuEnglishLabel={false}
              />
            }
          />
          <SettingRow
            title={t("T_SITE_SETTINGS_SOUND")}
            rightSlot={
              <RenderToggle
                value={sound}
                onChange={() => {
                  const next = !sound;
                  setSound(next);
                  updateField({ isSoundEffectsEnabled: next });
                }}
              />
            }
          />
          {/* Email notifications */}
          <SettingRow
            title={t("T_SITE_SETTINGS_EMAIL_NOTIFICATIONS")}
            rightSlot={
              <RenderToggle
                value={emailNotif}
                onChange={() => {
                  const next = !emailNotif;
                  setEmailNotif(next);
                  updateField({ isEmailNotificationsEnabled: next });
                }}
              />
            }
          />
          {/* Push / mobile notifications */}
          <SettingRow
            title={t("T_SITE_SETTINGS_NOTIFICATIONS")}
            rightSlot={
              <RenderToggle
                value={notifications}
                onChange={() => {
                  const next = !notifications;
                  setNotifications(next);
                  updateField({ isMobileNotificationsEnabled: next });
                }}
              />
            }
          />
          {/* Hand power meter */}
          <SettingRow
            title={t("T_SITE_SETTINGS_HAND_POWER_METER")}
            rightSlot={
              <RenderToggle
                value={handMeter}
                onChange={() => {
                  const next = !handMeter;
                  setHandMeter(next);
                  updateField({ isHandPowerMeterEnabled: next });
                }}
              />
            }
          />
          <SettingRow
            title={t("T_SITE_SETTINGS_FACEBOOK")}
            rightSlot={
              <RenderToggle
                value={facebook}
                onChange={() => {
                  const next = !facebook;
                  setFacebook(next);
                  // updateField({ isFacebookLinked: next }); // when backend supports
                  // toast(next ? "Facebook linked" : "Facebook unlinked");
                }}
              />
            }
          />
          {/* <TouchableOpacity onPress={onSave} style={{ alignSelf: "center" }}>
            <ImageBackground
              source={SaveButton}
              style={{ width: 160, height: 80 }}
              resizeMode="contain"
            >
              <View
                style={{
                  flex: 1,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text style={styles.text}>Save</Text>
              </View>
            </ImageBackground>
          </TouchableOpacity> */}
        </ScrollView>
      </ModalReusable>
    </>
  );
}

export default SettingsModal;

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    color: "#cdd6e6",
    fontSize: 15,
    flex: 1,
    marginRight: 20,
  },
  /* ---------- Toggle ---------- */
  toggleBg: {
    width: 110,
    height: 50,
    justifyContent: "center",
  },
  toggleBgImage: {
    borderRadius: 25,
  },
  knob: {
    position: "absolute",
    top: 1,
    width: 48,
    height: 48,
  },
  knobLabel: {
    alignSelf: "center",
    fontSize: 15,
    fontWeight: "700",
    color: "#fff",
  },

  /* ---------- Language Select ---------- */
  languageWrap: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0f2440",
    borderRadius: 12,
    paddingHorizontal: 12,
    height: 50,
  },
  flag: { width: 32, height: 20, marginRight: 8 },
  langLabel: { color: "#fff", fontSize: 16, marginRight: 6 },
  arrow: { width: 16, height: 16 },

  text: {
    fontSize: 18,
    fontWeight: "900",
    letterSpacing: 1,
    color: "#E0E0E0",
    textShadowColor: "rgba(0, 0, 0, 0.6)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 2,
    textTransform: "uppercase",
    paddingBottom: 2,
  },
  customBox: {
    padding: 16,
    borderRadius: 10,
    borderWidth: 2,
  },
  text1: { color: "#fff", fontSize: 20, fontWeight: "700" },
  text2: { color: "#cdd6e6", fontSize: 12 },
});
